/**
 * Maps Firebase / network errors to user-facing copy.
 * Used by DataFetchState, ServiceStatusBanner, and page-level error UI.
 */

const ERROR_PATTERNS = [
  {
    test: (msg) => /network|offline|failed to fetch|network-request-failed/i.test(msg),
    title: 'Connection problem',
    message: "We couldn't reach our servers. Check your internet connection and try again.",
    kind: 'network',
  },
  {
    test: (msg) => /unavailable|service unavailable|deadline-exceeded/i.test(msg),
    title: 'Service temporarily unavailable',
    message: 'PetsFostering is having trouble loading data right now. Please try again in a moment.',
    kind: 'unavailable',
  },
  {
    test: (msg) => /permission-denied|insufficient permissions/i.test(msg),
    title: 'Access restricted',
    message: "You don't have permission to view this. Try signing in again or contact support.",
    kind: 'permission',
  },
  {
    test: (msg) => /unauthenticated|auth\/|not authenticated/i.test(msg),
    title: 'Session expired',
    message: 'Please sign in again to continue.',
    kind: 'auth',
  },
  {
    test: (msg) => /quota|resource-exhausted/i.test(msg),
    title: 'High traffic',
    message: "We're experiencing high demand. Please wait a moment and try again.",
    kind: 'quota',
  },
];

const DEFAULT_ERROR = {
  title: 'Something went wrong',
  message: "We couldn't load this right now. Please try again.",
  kind: 'unknown',
};

export function mapServiceError(rawError) {
  const msg = typeof rawError === 'string' ? rawError : rawError?.message || '';
  if (!msg.trim()) return { ...DEFAULT_ERROR };

  const match = ERROR_PATTERNS.find((p) => p.test(msg));
  if (match) {
    return { title: match.title, message: match.message, kind: match.kind, raw: msg };
  }

  return {
    ...DEFAULT_ERROR,
    message: process.env.NODE_ENV === 'development' ? msg : DEFAULT_ERROR.message,
    raw: msg,
  };
}

export function isNetworkError(rawError) {
  const { kind } = mapServiceError(rawError);
  return kind === 'network' || kind === 'unavailable';
}
